import React, { useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Button, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { PropTypes } from 'prop-types';
import { LoadingButton } from '~/modules/common/components';
import { useHasFeatureFlag } from '~/modules/common/hooks';
import { DimensionObjectType } from '~/types';
import { NotReferencedProjectRoles } from './notReferencedProjectRoles';

const useStyles = makeStyles(theme => ({
  rowContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16
  },
  accordionWrapper: {
    flex: 1,
    overflowY: 'auto',
    paddingBottom: theme.spacing(1)
  },
  removeAllButton: {
    [theme.breakpoints.up('md')]: {
      paddingLeft: 0
    },
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(-2)
    },
    [theme.breakpoints.up(400)]: {
      whiteSpace: 'nowrap'
    }
  },
  loadingButton: {
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(-2),
      marginLeft: theme.spacing(0.5)
    }
  }
}));

const extractDimensionValueIds = (entries, dimensionId) =>
  entries.map(e => e[dimensionId]?.value?.id).filter(Boolean);

export const AdvancedRateCardActionComponent = ({
  onRemove,
  saveable,
  isSaving,
  onCancel,
  onSave,
  validateForm,
  actionComponentCustomProps
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const isPsaPrpRoleFilterOnTasksEnabled = useHasFeatureFlag({
    featureFlag: 'isPsaPrpRoleFilterOnTasksEnabled'
  });
  const {
    loadingMore,
    addButtonResourceKey,
    onAddEntryClick,
    readOnly,
    projectDimensions,
    projectId,
    projectRates,
    showMissingRoles
  } = actionComponentCustomProps;

  const handleSave = useCallback(async () => {
    if (validateForm) {
      const errors = await validateForm();

      if (Object.keys(errors).length) return;
    }
    onSave();
  }, [onSave, validateForm]);

  const removeAllRates = useCallback(() => {
    if (onRemove) onRemove();
  }, [onRemove]);

  const displayText = isPsaPrpRoleFilterOnTasksEnabled
    ? projectDimensions[0]?.displayText
    : addButtonResourceKey;

  const projectRoleDimensionIndex = projectDimensions.findIndex(
    ({ objectType }) => objectType === DimensionObjectType.ProjectRole
  );

  const onAddDimensionValueClick = useCallback(() => {
    onAddEntryClick(null);
  }, [onAddEntryClick]);

  return (
    <>
      <Grid item xs={12} container spacing={0}>
        {isPsaPrpRoleFilterOnTasksEnabled && (
          <Grid item xs={12}>
            {!readOnly && (
              <div className={classes.rowContainer}>
                {displayText && (
                  <Button
                    color="primary"
                    size="small"
                    onClick={onAddDimensionValueClick}
                    disabled={isSaving || loadingMore}
                  >
                    <Typography variant="body2">
                      {formatMessage(
                        { id: 'advancedRateCard.addDimensionValue' },
                        { displayText }
                      )}
                    </Typography>
                  </Button>
                )}

                {projectRoleDimensionIndex !== -1 && (
                  <div className={classes.accordionWrapper}>
                    <NotReferencedProjectRoles
                      projectId={projectId}
                      configuredProjectRoles={extractDimensionValueIds(
                        projectRates,
                        projectDimensions[projectRoleDimensionIndex].id
                      )}
                      canAddRole={projectRoleDimensionIndex === 0}
                      showMissingRoles={showMissingRoles}
                      onAddRole={onAddEntryClick}
                    />
                  </div>
                )}
              </div>
            )}
          </Grid>
        )}
        {!isPsaPrpRoleFilterOnTasksEnabled && (
          <Grid
            item
            xs={6}
            sm={7}
            md={6}
            container
            justifyContent="flex-start"
            spacing={0}
          >
            {!readOnly && addButtonResourceKey && (
              <Button
                color="primary"
                size="small"
                onClick={onAddEntryClick}
                disabled={isSaving || loadingMore}
              >
                <Typography variant="body2">
                  {formatMessage(
                    { id: 'advancedRateCard.addDimensionValue' },
                    { displayText: addButtonResourceKey }
                  )}
                </Typography>
              </Button>
            )}
          </Grid>
        )}
      </Grid>
      <Grid item xs={12} container spacing={0}>
        <Grid
          item
          xs={6}
          sm={7}
          md={6}
          container
          justify="flex-start"
          spacing={0}
        >
          {!readOnly && onRemove ? (
            <Button
              color="secondary"
              onClick={removeAllRates}
              className={classes.removeAllButton}
              disabled={isSaving || loadingMore}
              data-qe-id="RemoveAllRates"
            >
              <FormattedMessage id="advancedRateCard.removeAllRates" />
            </Button>
          ) : null}
        </Grid>
        <Grid
          item
          xs={6}
          sm={5}
          md={6}
          container
          justify="flex-end"
          spacing={0}
        >
          <Button
            onClick={onCancel}
            disabled={isSaving || loadingMore}
            data-qe-id="Cancel"
          >
            <FormattedMessage
              id={readOnly ? 'button.close' : 'button.cancel'}
            />
          </Button>
          {!readOnly && (
            <LoadingButton
              color="primary"
              onClick={handleSave}
              className={classes.loadingButton}
              disabled={!saveable || isSaving || loadingMore}
              isLoading={isSaving}
              data-qe-id="Save"
            >
              <FormattedMessage id="button.save" />
            </LoadingButton>
          )}
        </Grid>
      </Grid>
    </>
  );
};
AdvancedRateCardActionComponent.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  onRemove: PropTypes.func,
  saveable: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool,
  validateForm: PropTypes.func,
  actionComponentCustomProps: PropTypes.object
};

export default AdvancedRateCardActionComponent;
